import styles from "../styles/floating-timer.css?inline";
import nightStyles from "../styles/night.css?inline";
import appearanceStyles from "../styles/appearance.css?inline";
import { syncAppearanceDocument } from "./appearance.js";
import { t, getLanguage } from "../core/i18n.js";
import { runtime } from "../core/runtime.js";
import { loadJSON, saveJSON } from "../core/storage.js";
import { toast } from "../core/navigation.js";
import { icon } from "./icons.js";
import { timerDisplay } from "./views.js";
import { startPom, pausePom, resetPom, tick } from "./timers.js";
import { pomodoroArt } from "./pomodoro-art.js";
export { pomodoroArt } from "./pomodoro-art.js";

let hidden = null;
let floatingWindow = null;
let floatingInterval = null;
let opening = false;
let signature = "";
let expanded = false;

export function timerHidden() {
  // Read after workspace storage has selected the account namespace.
  if (hidden === null) hidden = loadJSON("dayx:timer-hidden", false) === true;
  return hidden;
}
export const floatingTimerSupported = () =>
  !!window.documentPictureInPicture?.requestWindow;

export function timerVisibilityButton() {
  timerHidden();
  return `<button type="button" class="timer-visibility" data-timer-visibility aria-pressed="${hidden}" aria-label="${t(hidden ? "Show time" : "Hide time")}" title="${t(hidden ? "Show time" : "Hide time")}">${icon(hidden ? "eye" : "eye-off")}<span>${t(hidden ? "Show time" : "Hide time")}</span></button>`;
}

export function toggleTimerVisibility() {
  hidden = !timerHidden();
  saveJSON("dayx:timer-hidden", hidden);
  document.getElementById("mainTimer")?.toggleAttribute("hidden", hidden);
  document
    .getElementById("pomodoroArtwork")
    ?.toggleAttribute("hidden", !hidden);
  document.querySelectorAll("[data-timer-visibility]").forEach((button) => {
    button.setAttribute("aria-pressed", String(hidden));
    button.setAttribute("aria-label", t(hidden ? "Show time" : "Hide time"));
    button.setAttribute("title", t(hidden ? "Show time" : "Hide time"));
    button.innerHTML = `${icon(hidden ? "eye" : "eye-off")}<span>${t(hidden ? "Show time" : "Hide time")}</span>`;
  });
  syncFloatingTimer();
}

export async function openFloatingTimer() {
  if (floatingWindow && !floatingWindow.closed) {
    floatingWindow.focus();
    return;
  }
  if (opening) return;
  if (!floatingTimerSupported()) {
    toast(t("Open DayX in desktop Chrome or Edge to use the floating timer."));
    return;
  }
  opening = true;
  try {
    const pip = await window.documentPictureInPicture.requestWindow({
      width: 200,
      height: 48,
      preferInitialWindowPlacement: true,
    });
    floatingWindow = pip;
    expanded = false;
    pip.document.title = "DayX · Pomodoro";
    pip.document.documentElement.lang = getLanguage();
    const style = pip.document.createElement("style");
    style.textContent = nightStyles + "\n" + styles + "\n" + appearanceStyles;
    pip.document.head.append(style);
    syncAppearanceDocument(pip.document);
    pip.document.body.className = "floating-timer-body";
    pip.document.body.innerHTML =
      '<main class="floating-timer" aria-label="Pomodoro"></main>';
    signature = "";
    // Keep the size button and details in sync with manual border resizing.
    pip.addEventListener("resize", () => {
      expanded = pip.innerWidth >= 220 && pip.innerHeight >= 150;
      syncFloatingTimer();
    });
    pip.document.body.addEventListener("click", (event) => {
      const button = event.target.closest("button");
      if (!button) return;
      if (button.hasAttribute("data-float-close")) {
        pip.close();
        return;
      }
      if (button.hasAttribute("data-float-size")) {
        expanded = !expanded;
        // Window resizing is permitted here because this is a user click.
        // resizeTo uses outer dimensions; keep the requested content size
        // consistent across browser borders, title bars, and display scaling.
        const chromeWidth = pip.outerWidth - pip.innerWidth;
        const chromeHeight = pip.outerHeight - pip.innerHeight;
        pip.resizeTo(
          (expanded ? 260 : 200) + chromeWidth,
          (expanded ? 180 : 48) + chromeHeight,
        );
      }
      if (button.hasAttribute("data-float-play"))
        runtime.focusTimer?.running ? pausePom() : startPom();
      if (button.hasAttribute("data-float-reset")) resetPom();
      syncFloatingTimer();
    });
    pip.document.addEventListener("keydown", (event) => {
      if (event.code === "Space" && !event.target.closest("button")) {
        event.preventDefault();
        runtime.focusTimer?.running ? pausePom() : startPom();
      }
    });
    // Use the visible PiP window's clock even while the original tab is hidden.
    floatingInterval = pip.setInterval(() => {
      tick();
      syncFloatingTimer();
    }, 250);
    pip.addEventListener(
      "pagehide",
      () => {
        pip.clearInterval(floatingInterval);
        floatingInterval = null;
        if (floatingWindow === pip) floatingWindow = null;
        signature = "";
      },
      { once: true },
    );
    syncFloatingTimer();
  } catch {
    floatingWindow?.close();
    floatingWindow = null;
    toast(t("The floating timer could not open. Please try again."));
  } finally {
    opening = false;
  }
}

export function syncFloatingTimer() {
  document.querySelectorAll("[data-focus-progress]").forEach((el) => {
    el.style.strokeDasharray = `${timerProgress()} 100`;
  });
  if (!floatingWindow || floatingWindow.closed) return;
  const root = floatingWindow.document.querySelector(".floating-timer");
  if (!root) return;
  const f = runtime.focusTimer;
  const running = !!f?.running;
  const mode = f?.mode || "work";
  const total =
    f?.totalBlocks ||
    runtime.state.pom.blocks.reduce((sum, b) => sum + b.repeats, 0);
  const nextSignature = JSON.stringify([
    running,
    mode,
    f?.block,
    f?.finished,
    getLanguage(),
    total,
    expanded,
  ]);
  if (signature !== nextSignature) {
    const focused =
      floatingWindow.document.activeElement?.getAttribute("data-focus-id");
    signature = nextSignature;
    floatingWindow.document.documentElement.lang = getLanguage();
    root.classList.toggle("expanded", expanded);
    const sizeLabel = t(expanded ? "Compact timer" : "Expand timer");
    const playLabel = t(running ? "PAUSE" : f?.paused ? "RESUME" : "START");
    root.innerHTML = `<header class="floating-timer-head"><span class="floating-brand">DayX<span> / ${t("Pomodoro")}</span></span><div class="floating-head-actions"><button type="button" data-float-size data-focus-id="size" aria-label="${sizeLabel}" title="${sizeLabel}">${icon(expanded ? "minimize" : "maximize")}</button><button type="button" data-float-close aria-label="${t("Close floating timer")}" title="${t("Close floating timer")}">${icon("close")}</button></div></header>
      <div class="floating-timer-stage"><div class="floating-sculpture">${pomodoroArt()}</div><div class="floating-face"><span class="floating-time" role="timer"></span><span class="floating-phase">${t(f?.finished ? "COMPLETE" : f?.paused ? "Paused" : mode === "rest" ? "REST TIME" : "FOCUS TIME")}</span></div></div>
      <div class="floating-controls"><button type="button" class="floating-play" data-float-play data-focus-id="play" aria-label="${playLabel}" title="${playLabel}">${icon(running ? "pause" : "play")}<span>${playLabel}</span></button><button type="button" class="floating-reset" data-float-reset data-focus-id="reset" aria-label="${t("RESET")}" title="${t("RESET")}">${icon("reset")}</button><button type="button" class="floating-mini-size" data-float-size data-focus-id="mini-size" aria-label="${sizeLabel}" title="${sizeLabel}">${icon(expanded ? "minimize" : "maximize")}</button></div>
      ${expanded ? `<footer class="floating-footer"><span>${t("Current block")} ${f?.block || 1} / ${total}</span></footer>` : ""}<div class="floating-progress-track" aria-hidden="true"><i class="floating-progress-fill"></i></div>`;
    if (focused) {
      let next = root.querySelector(`[data-focus-id="${focused}"]`);
      if (
        !next?.getClientRects().length &&
        (focused === "size" || focused === "mini-size")
      )
        next = [...root.querySelectorAll("[data-float-size]")].find(
          (button) => button.getClientRects().length,
        );
      next?.focus({ preventScroll: true });
    }
  }
  const time = root.querySelector(".floating-time");
  if (time) time.textContent = timerDisplay();
  root.querySelector(".floating-progress-fill").style.width =
    `${timerProgress()}%`;
}

export function timerProgress() {
  const f = runtime.focusTimer;
  if (!f) return 0;
  if (f.finished) return 100;
  const duration = (f.sequence[f.index]?.[f.mode] || 0) * 1000;
  const remaining = f.running ? Math.max(0, f.endAt - Date.now()) : f.remaining;
  return duration > 0
    ? Math.max(0, Math.min(100, (1 - remaining / duration) * 100))
    : 0;
}
